import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Header from './header';
import LoaderSpinner from '../loaderSpinner';
import SideBarMenu from './sideBarMenu';
import { logger } from '../../utils/logger';
import {
  getAllGIUsers,
  updateGIUserStatus,
  ROLE_LABELS,
} from '../../services/firestore';

const PORTAL_LABELS = {
  gi: 'GI Specialist',
  pcp: 'PCP',
};

function PortalUsers() {
  const [users, setUsers] = useState([]);
  const [ShowLoader, setShowLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [filterPortal, setFilterPortal] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [togglingId, setTogglingId] = useState(null);

  const fetchUsers = async () => {
    try {
      setShowLoader(true);
      const data = await getAllGIUsers();
      setUsers(data);
    } catch (error) {
      logger.error('Error fetching portal users:', error);
      toast.error('Failed to load portal users', { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleToggleStatus = async (user) => {
    const currentStatus = user.accountStatus || 'active';
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    setTogglingId(user.id);
    try {
      await updateGIUserStatus(user.id, newStatus);
      setUsers(prev => prev.map(u =>
        u.id === user.id ? { ...u, accountStatus: newStatus } : u
      ));
      toast.success(
        `${user.fullName} is now ${newStatus}`,
        { position: toast.POSITION.TOP_RIGHT }
      );
    } catch (error) {
      logger.error('Toggle error:', error);
      toast.error('Failed to update status', { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setTogglingId(null);
    }
  };

  // Filtering + Search
  const filteredUsers = users.filter(u => {
    if (filterPortal !== 'all' && (u.portal || 'gi') !== filterPortal) return false;
    if (filterStatus !== 'all') {
      const status = u.accountStatus || 'active';
      if (status !== filterStatus) return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      if (
        !(u.fullName || '').toLowerCase().includes(q) &&
        !(u.email || '').toLowerCase().includes(q) &&
        !(u.phoneNumber || '').toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / recordsPerPage);
  const currentPageGroup = Math.ceil(currentPage / 3);

  const handleRecordsPerPageChange = (event) => {
    setRecordsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleNextGroup = () => { if (currentPage < totalPages) setCurrentPage(currentPage + 1); };
  const handlePrevGroup = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };

  const renderTableBody = () => {
    const start = (currentPage - 1) * recordsPerPage;
    const end = start + recordsPerPage;
    const pageData = filteredUsers.slice(start, end);

    if (pageData.length === 0) {
      return (
        <tr>
          <td colSpan="7" style={{ textAlign: 'center', color: '#8D8D8D', padding: '40px' }}>
            No portal users found.
          </td>
        </tr>
      );
    }

    return pageData.map((user, index) => {
      const status = user.accountStatus || 'active';
      const portal = user.portal || 'gi';
      return (
        <tr key={user.id || index}>
          <td>{user.fullName || '—'}</td>
          <td>{user.email || '—'}</td>
          <td>{user.phoneNumber || '—'}</td>
          <td>
            <span className={`signup-portal-badge signup-portal-${portal}`}>
              {PORTAL_LABELS[portal] || portal}
            </span>
          </td>
          <td>{ROLE_LABELS[user.role] || user.role || '—'}</td>
          <td>
            <span className={`portal-status-badge portal-status-${status}`}>
              {status === 'active' ? 'Active' : 'Inactive'}
            </span>
          </td>
          <td>
            <div className="switch-container">
              <input
                type="checkbox"
                id={`toggle-${user.id}`}
                className="switch"
                checked={status === 'active'}
                onChange={() => handleToggleStatus(user)}
                disabled={togglingId === user.id}
              />
              <label className="switch-slider" htmlFor={`toggle-${user.id}`}>
                <span className="switch-knob"></span>
              </label>
            </div>
          </td>
        </tr>
      );
    });
  };

  const renderPagination = () => {
    let pages = [];
    const startPage = (currentPageGroup - 1) * 3 + 1;
    const endPage = Math.min(startPage + 2, totalPages);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`page-button ${i === currentPage ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button onClick={handlePrevGroup} disabled={currentPage === 1}>Previous</button>
        {pages}
        <button onClick={handleNextGroup} disabled={currentPage === totalPages}>Next</button>
      </div>
    );
  };

  const activeCount = users.filter(u => (u.accountStatus || 'active') === 'active').length;
  const inactiveCount = users.filter(u => u.accountStatus === 'inactive').length;

  return (
    <div>
      {ShowLoader && <LoaderSpinner />}
      <div className='container-fluid p-0 adminDashboardMain'>
        <SideBarMenu />
        <div className='container-fluid p-0 contentBody'>
          <Header />
          <div className="innerBodyBox bgInnerDiv">
            <div className="contentInnerBody">
              <div className="titleDiv">
                <h2>Portal Users</h2>
                <ul className="titleRightSideDiv">
                  <li>
                    <input
                      type="text"
                      placeholder="Search by name, email or phone..."
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                      style={{ padding: '6px 12px', border: '1px solid #E6E6E6', borderRadius: '6px', fontSize: '13px', width: '240px' }}
                    />
                  </li>
                  <li>
                    <div className="table-options">
                      <label htmlFor="filterPortal">Portal:</label>
                      <select
                        id="filterPortal"
                        value={filterPortal}
                        onChange={(e) => { setFilterPortal(e.target.value); setCurrentPage(1); }}
                        style={{ border: 0, background: 'transparent', cursor: 'pointer' }}
                      >
                        <option value="all">All</option>
                        <option value="gi">GI Specialist</option>
                        <option value="pcp">PCP</option>
                      </select>
                    </div>
                  </li>
                  <li>
                    <div className="table-options">
                      <label htmlFor="filterStatusPortal">Status:</label>
                      <select
                        id="filterStatusPortal"
                        value={filterStatus}
                        onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                        style={{ border: 0, background: 'transparent', cursor: 'pointer' }}
                      >
                        <option value="all">All ({users.length})</option>
                        <option value="active">Active ({activeCount})</option>
                        <option value="inactive">Inactive ({inactiveCount})</option>
                      </select>
                    </div>
                  </li>
                  <li>Total: {filteredUsers.length}</li>
                  <li>
                    <div className="table-options">
                      <label htmlFor="recordsPerPagePortal">Showing</label>
                      <select
                        id="recordsPerPagePortal"
                        value={recordsPerPage}
                        onChange={handleRecordsPerPageChange}
                      >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="contentMainBox">
                <div className="table-container">
                  <table id="portalUsersTable">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Portal</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderTableBody()}
                    </tbody>
                  </table>
                </div>
                <div className="paginationMain">
                  {renderPagination()}
                  <span className="totalPageDiv">Total Pages: {totalPages}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PortalUsers;
